/**
 * Logging-severity helpers for the household-hydration path in `App.tsx`.
 *
 * Two conditions there used to be sent to Sentry at ERROR severity but are
 * actually benign/expected, so they should be breadcrumbs instead — context for
 * any later real error, without themselves tripping error-rate alerts:
 *
 *  - a first-time social user with no household doc yet (→ S23.2 onboarding), and
 *  - a transient Firestore "client is offline" / `unavailable` blip the SDK
 *    auto-recovers from (common on mobile).
 *
 * These pure predicates decide error-vs-breadcrumb and are unit-tested.
 *
 * [Sentry 131606349 (household not found) / 131603075 (client offline)]
 *
 * @module lib/household-log
 */

interface UserCreationMetadata {
  creationTime?: string;
  lastSignInTime?: string;
}

/**
 * Max gap between account creation and this sign-in to still count as the same
 * (account-creation) session. Firebase stamps `creationTime` and
 * `lastSignInTime` identically on the sign-in that creates the account; a small
 * window absorbs sub-second/clock differences without matching a returning user.
 */
const NEW_USER_SESSION_WINDOW_MS = 60_000;

/**
 * True when the Firebase account was created in this same sign-in session
 * (`creationTime` ≈ `lastSignInTime`). For such a user a missing household doc
 * is the EXPECTED pre-onboarding state (first-time social sign-in → S23.2), not
 * an error. An established account (sign-in well after creation) missing its
 * household is a real problem and should still log as an error.
 */
export function isNewlyCreatedUser(metadata: UserCreationMetadata): boolean {
  const { creationTime, lastSignInTime } = metadata;
  if (!creationTime || !lastSignInTime) return false;
  const createdMs = Date.parse(creationTime);
  const lastSignInMs = Date.parse(lastSignInTime);
  if (Number.isNaN(createdMs) || Number.isNaN(lastSignInMs)) return false;
  return Math.abs(lastSignInMs - createdMs) < NEW_USER_SESSION_WINDOW_MS;
}

/**
 * True for a transient Firestore "client is offline" / `unavailable` failure.
 * The SDK reconnects automatically, so this is benign network noise (common on
 * mobile) — a breadcrumb rather than an error.
 */
export function isFirestoreOffline(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const code = (error as { code?: unknown }).code;
  const message = (error as { message?: unknown }).message;
  return (
    code === 'unavailable' || (typeof message === 'string' && message.includes('client is offline'))
  );
}
